import { useRef, useCallback, useEffect } from 'react';
import { useDrop } from 'react-dnd';

import { DropProvider } from 'hb-builder/contexts';
import {
  useBuilderContext,
  useComponentContext,
  useComponentsContext,
  useLayoutContext,
  useBuilderConfigContext,
  usePanelContext,
} from 'hb-builder/hooks';
import { createNewItem } from 'hb-builder/utils/createNewItem';
import { findItem } from 'hb-builder/utils/item';

import Placeholder from './Placeholder';

const SCROLL_VALUE = 7;

const UNSELECTABLE_COMPONENTS = ['Section', 'Text'];

const DropWithPlaceholder = ({
  children,
  dropRef,
  isOver,
  item,
  placeholderPositions,
  placeholderPositionRef,
  overItem,
  overType,
}) => {
  const canDrop = useCallback(
    (placeholderPosition) => {
      placeholderPositionRef.current = null;

      if (!isOver) {
        return false;
      }

      placeholderPositionRef.current = placeholderPosition;

      return true;
    },
    [isOver, placeholderPositionRef],
  );

  return (
    <Placeholder
      canDrop={canDrop}
      dropRef={dropRef}
      isOver={isOver}
      item={item}
      overItem={overItem}
      overType={overType}
      placeholderPositions={placeholderPositions}
    >
      {children}
    </Placeholder>
  );
};

const scrollOnDrag = ({ element, direction }) => {
  const scrollValue = direction === 'down' ? SCROLL_VALUE : -SCROLL_VALUE;

  element.scrollTo(0, element.scrollTop + scrollValue);
};

const Drop = ({
  accept,
  children,
  isNewStructure = false,
  placeholderPositions = ['bottom', 'top'],
  noPlaceholder = false,
}) => {
  const {
    addItem,
    getLastSectionItem,
    getPath,
    removeItem,
    setSelectedItemId,
  } = useBuilderContext();
  const { setSelectedPanelTab } = usePanelContext();
  const { item: overItem, type: overType } = useComponentContext();
  const { getBodyItem } = useBuilderContext();
  const { components } = useComponentsContext();
  const { features } = useBuilderConfigContext();
  const { centerRef, documentRef } = useLayoutContext();

  const bodyWidth = getBodyItem()?.attributes?.bodyWidth;
  const placeholderPositionRef = useRef(null);

  const [{ canDrop, dropResult, isOver, item, itemType }, drop] = useDrop({
    accept,
    canDrop: ({ files }) => files || item?.id !== overItem?.id,
    drop: ({ item: component, files }, monitor) => {
      if (monitor.didDrop() || !monitor.canDrop()) {
        return undefined;
      }

      // save placeholder position even if uploading the image
      const stagedPlaceholderPosition = placeholderPositionRef.current;
      let item;

      if (files && components) {
        const ImageComponent =
          components.Image || components.PageImage || components.EmailImage;

        item = {
          ...ImageComponent.item,
          attributes: {
            ...ImageComponent.item.attributes,
            __loading: files[0],
          },
        };
      }

      if (component) {
        item = component;
      }

      const overItemEmptyChildren =
        overItem.children &&
        (overItem.category === 'dynamic'
          ? false
          : overItem.children.filter(
              (child) =>
                child?.attributes?.isEditable !== false &&
                // @TODO: check what happens if it is a EmailImage ??
                // consider the document empty when there are only injected Email specific components (EmailFooter, EmailMirrorLink and EmailActivationButton)
                !child.type?.startsWith('Email'),
            ).length === 0);

      if (!overItemEmptyChildren && !stagedPlaceholderPosition) {
        return undefined;
      }

      // Remove existing component when we move it
      if (component && component.id) {
        removeItem(component.id);
      }

      const newItem = createNewItem(component, { isDuplicate: true });

      if (overItem?.type === 'Body') {
        const lastSection = getLastSectionItem();
        const defaultItemParams = {
          id: lastSection?.id,
          direction: 1,
          path: ['children', 0],
        };

        addItem({
          ...defaultItemParams,
          ...(component.category === 'single'
            ? { item: addSingleItem(newItem) }
            : { item: newItem }),
        });

        return;
      }

      addItem({
        ...(component.category === 'single' && overType === 'section'
          ? { item: addSingleItem(newItem) }
          : { item: newItem }),
        ...(overItemEmptyChildren
          ? {
              path: getPath(overItem.id),
            }
          : {
              id: overItem.id,
              direction:
                placeholderPositionRef.current === 'bottom' || isNewStructure
                  ? 1
                  : 0,
            }),
      });

      setSelectedItemId(null);
      setSelectedPanelTab('components');

      // Reset placeholder position
      placeholderPositionRef.current = null;

      return findItem(newItem, (child) => child.type === item.type);
    },
    hover: (_, monitor) => {
      // IE 8 and lower does not support window.innerHeight
      const visibleHeight =
        window?.innerHeight || document.documentElement.offsetHeight;
      // First quarter (top of the page)
      const topVisible = visibleHeight / 5;
      // Last quarter (bottom of the page)
      const bottomVisible = visibleHeight - visibleHeight / 5;

      const monitorOffset = monitor.getClientOffset();

      if (!monitorOffset) {
        return undefined;
      }

      const draggedComponentTop = monitorOffset.y;

      if (documentRef.current) {
        const { height: documentFullHeight } =
          documentRef.current.getBoundingClientRect();

        if (
          draggedComponentTop &&
          centerRef.current &&
          documentFullHeight > visibleHeight
        ) {
          if (draggedComponentTop > bottomVisible) {
            return scrollOnDrag({
              element: centerRef.current.contentWrapperEl,
              direction: 'down',
            });
          }
          if (draggedComponentTop < topVisible) {
            return scrollOnDrag({
              element: centerRef.current.contentWrapperEl,
              direction: 'up',
            });
          }
        }
      }

      return undefined;
    },
    collect: (monitor) => ({
      canDrop: monitor.canDrop(),
      dropResult: monitor.getDropResult(),
      isOver: monitor.isOver({ shallow: true }),
      item: monitor.getItem()?.item,
      itemType: monitor.getItemType(),
    }),
  });

  const addSingleItem = (newItem) =>
    createNewItem(
      components.Section.item({
        children: [
          createNewItem(components.Column.item({ children: [newItem] }), {
            isDuplicate: true,
          }),
        ],
        features: {
          ...features,
          section: {
            ...features?.section,
            name: 'FullWidth',
            sectionWidth: bodyWidth,
          },
        },
      }),
      { isDuplicate: true },
    );

  useEffect(() => {
    if (dropResult?.id && !UNSELECTABLE_COMPONENTS.includes(dropResult?.type)) {
      setSelectedItemId(dropResult.id);
    }
  }, [dropResult]);

  const isOverAnotherComponent = isOver && item.id !== overItem.id;

  const renderChildren = () =>
    typeof children === 'function'
      ? children({
          item,
          itemType,
          overItem,
          isOver: isOverAnotherComponent,
          canDrop,
          ref: drop,
        })
      : children;

  return (
    <DropProvider isOver={isOverAnotherComponent}>
      {noPlaceholder ? (
        renderChildren()
      ) : (
        <DropWithPlaceholder
          dropRef={drop}
          isOver={isOverAnotherComponent}
          item={item}
          placeholderPositions={placeholderPositions}
          placeholderPositionRef={placeholderPositionRef}
          overItem={overItem}
          overType={overType}
        >
          {renderChildren()}
        </DropWithPlaceholder>
      )}
    </DropProvider>
  );
};

export default Drop;
